import { create } from 'zustand';
import projectTemplateService from '../services/projectTemplateService';

const useProjectTemplateStore = create((set, get) => ({
  // State
  templates: [],
  currentTemplate: null,
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  },
  filters: {
    category: 'all',
    search: '',
    sortBy: 'usageCount',
    sortOrder: 'desc'
  },
  stats: null,

  // Actions
  setLoading: (loading) => set({ isLoading: loading }),
  
  setError: (error) => set({ error }),
  
  clearError: () => set({ error: null }),

  setFilters: (newFilters) => set((state) => ({
    filters: { ...state.filters, ...newFilters }
  })),

  resetFilters: () => set({
    filters: {
      category: 'all',
      search: '',
      sortBy: 'usageCount',
      sortOrder: 'desc'
    }
  }),

  // Fetch all templates
  fetchTemplates: async (options = {}) => {
    const state = get();
    const requestOptions = {
      page: options.page || state.pagination.currentPage,
      limit: options.limit || state.pagination.itemsPerPage,
      ...state.filters,
      ...options
    };

    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.getAllTemplates(requestOptions);
      
      // Handle the response structure { status: 'success', meta, data: { templates } }
      const templates = response.data?.templates || [];
      const meta = response.meta || {};
      
      set({
        templates: templates,
        pagination: {
          currentPage: meta.page || 1,
          totalPages: meta.totalPages || 1,
          totalItems: meta.total || 0,
          itemsPerPage: meta.limit || 10,
          hasNextPage: meta.page < meta.totalPages,
          hasPrevPage: meta.page > 1
        },
        isLoading: false
      });

      return response;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to fetch templates',
        isLoading: false,
        templates: []
      });
      throw error;
    }
  },

  // Fetch template by ID
  fetchTemplateById: async (id) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.getTemplateById(id);
      
      const template = response.data?.template || null;
      
      set({
        currentTemplate: template,
        isLoading: false
      });

      return template;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to fetch template',
        isLoading: false,
        currentTemplate: null
      });
      throw error;
    }
  },

  // Create new template
  createTemplate: async (templateData) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.createTemplate(templateData);
      const template = response.data?.template || null;
      
      // Add to the beginning of templates list
      set((state) => ({
        templates: template ? [template, ...state.templates] : state.templates,
        isLoading: false
      }));

      return template;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to create template',
        isLoading: false
      });
      throw error;
    }
  },

  // Update template
  updateTemplate: async (id, templateData) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.updateTemplate(id, templateData);
      
      const template = response.data?.template || null;
      
      // Update in templates list
      set((state) => ({
        templates: template ? state.templates.map((t) =>
          t._id === id ? template : t
        ) : state.templates,
        currentTemplate: state.currentTemplate?._id === id ? template : state.currentTemplate,
        isLoading: false
      }));

      return template;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to update template',
        isLoading: false
      });
      throw error;
    }
  },

  // Delete template
  deleteTemplate: async (id) => {
    set({ isLoading: true, error: null });

    try {
      await projectTemplateService.deleteTemplate(id);
      
      // Remove from templates list
      set((state) => ({
        templates: state.templates.filter((template) => template._id !== id),
        currentTemplate: state.currentTemplate?._id === id ? null : state.currentTemplate,
        isLoading: false
      }));

      return true;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to delete template',
        isLoading: false
      });
      throw error;
    }
  },

  // Duplicate template
  duplicateTemplate: async (id, newName) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.duplicateTemplate(id, newName);
      
      const template = response.data?.template || null;
      
      // Add to the beginning of templates list
      set((state) => ({
        templates: template ? [template, ...state.templates] : state.templates,
        isLoading: false
      }));

      return template;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to duplicate template',
        isLoading: false
      });
      throw error;
    }
  },

  // Create project from template
  createProjectFromTemplate: async (templateId, projectData) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.createProjectFromTemplate(templateId, projectData);
      
      const project = response.data?.project || null;
      
      // Update template usage count optimistically
      set((state) => ({
        templates: state.templates.map((template) =>
          template._id === templateId 
            ? { ...template, usageCount: (template.usageCount || 0) + 1 }
            : template
        ),
        isLoading: false
      }));

      return project;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to create project from template',
        isLoading: false
      });
      throw error;
    }
  },

  // Create template from project
  createTemplateFromProject: async (projectId, templateData) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.createTemplateFromProject(projectId, templateData);
      
      const template = response.data?.template || null;
      
      // Add to the beginning of templates list
      set((state) => ({
        templates: template ? [template, ...state.templates] : state.templates,
        isLoading: false
      }));

      return template;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to create template from project',
        isLoading: false
      });
      throw error;
    }
  },

  // Duplicate project
  duplicateProject: async (projectId, projectData) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.duplicateProject(projectId, projectData);
      set({ isLoading: false });
      return response.data?.project || null;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to duplicate project',
        isLoading: false
      });
      throw error;
    }
  },

  // Search templates
  searchTemplates: async (searchTerm, options = {}) => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.searchTemplates(searchTerm, options);
      
      const templates = response.data?.templates || [];
      
      set({
        templates: templates,
        isLoading: false
      });

      return templates;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to search templates',
        isLoading: false,
        templates: []
      });
      throw error;
    }
  },

  // Fetch template statistics
  fetchTemplateStats: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.getTemplateStats();
      
      const stats = response.data?.stats || null;
      
      set({
        stats: stats,
        isLoading: false
      });

      return stats;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to fetch template statistics',
        isLoading: false,
        stats: null
      });
      throw error;
    }
  },

  // Create default templates
  createDefaultTemplates: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await projectTemplateService.createDefaultTemplates();
      
      // Refresh templates list
      await get().fetchTemplates();

      return response.data?.templates || [];
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Failed to create default templates',
        isLoading: false
      });
      throw error;
    }
  },

  // Set current template
  setCurrentTemplate: (template) => set({ currentTemplate: template }),

  // Clear current template
  clearCurrentTemplate: () => set({ currentTemplate: null }),

  // Clear all data
  clearAll: () => set({
    templates: [],
    currentTemplate: null,
    isLoading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
      hasNextPage: false,
      hasPrevPage: false
    },
    stats: null
  })
}));

export default useProjectTemplateStore;